import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["payloadJson"],
  _excluded2 = ["refineAction"];
import { convertV1ActionToV2ToolInput } from 'copilot-toolkit/actions/convertV1ActionToV2ToolInput';
import { CONTENT_REWRITE, TEXT_GENERATION } from 'copilot-toolkit/constants/CopilotInAppActionTypes';
import { safeParseJson } from './jsonUtil';

/**
 * A message action is identified as being in collaborate mode if it is using
 * one of our plugins or is a follow-up to a previous message (e.g. via the
 * Refine button in collab mode).
 */
export const isCollaborateMode = (source, type) => {
  return source === 'PLUGIN' || source === 'ACTION' && type === 'FOLLOWUP';
};

/**
 * For Collborate Mode in Copilot V2, we need to pass the editor plugin data
 * in freeform user messages in a serialized JSON string.
 *
 * We assume any freeform user message in collab mode might be a follow-up to
 * refine the text. The LLM will make the determination of whether it was a
 * follow-up or an unrelated ask.
 */
export const getMaybeFreeformContextFromExtraIntentProperties = ({
  extraIntentProperties,
  message
}) => {
  if (!(extraIntentProperties !== null && extraIntentProperties !== void 0 && extraIntentProperties.content_rewrite)) {
    return {};
  }
  return {
    selectedText: extraIntentProperties.content_rewrite.text,
    operation: message,
    precedingText: extraIntentProperties.content_rewrite.precedingText,
    followingText: extraIntentProperties.content_rewrite.followingText,
    language: extraIntentProperties.content_rewrite.contentLanguage
  };
};

/**
 * Parse the message action and map it to a tool input for the LLM.
 */
export const getToolInputFromMessageAction = (collabMode, messageAction) => {
  const _messageAction$data = messageAction.data,
    {
      payloadJson
    } = _messageAction$data,
    messageActionData = _objectWithoutPropertiesLoose(_messageAction$data, _excluded);

  // Parse the payload if it exists. This is usually from an action button
  // we render in a message section.
  const payload = payloadJson ? safeParseJson(payloadJson) : {};
  if (collabMode) {
    var _messageAction$data2, _messageAction$data3;
    // Extend the message action data with the payload.
    // We don't always have the payload, but it should take
    // precedence over the message action data when available.
    const actionData = Object.assign({}, messageActionData, payload);

    // TODO: Align the backend schema to match the standardized format outlined by https://tools.hubteam.com/chadmin-ui/commands/content_rewrite and https://tools.hubteam.com/chadmin-ui/commands/text_generate

    if ((messageAction === null || messageAction === void 0 || (_messageAction$data2 = messageAction.data) === null || _messageAction$data2 === void 0 ? void 0 : _messageAction$data2.intent) === CONTENT_REWRITE) {
      // Convert the payload to ContentRewriteAction which should be the standardized format outlined by https://tools.hubteam.com/chadmin-ui/commands/content_rewrite
      return convertV1ActionToV2ToolInput({
        intent: CONTENT_REWRITE,
        text: actionData.selectedText || actionData.text,
        // It is selectedText in the payload, but text in the message action data
        operation: actionData.operation,
        editorText: actionData.editorText,
        precedingText: actionData.precedingText,
        followingText: actionData.followingText,
        brandVoiceChannel: actionData.brandVoiceChannel || actionData.voiceChannel,
        brandVoiceSubChannel: actionData.brandVoiceSubChannel || actionData.voiceSubChannel,
        businessUnitId: actionData.businessUnitId,
        contentLanguage: actionData.contentLanguage || actionData.language
      });
    } else if ((messageAction === null || messageAction === void 0 || (_messageAction$data3 = messageAction.data) === null || _messageAction$data3 === void 0 ? void 0 : _messageAction$data3.intent) === TEXT_GENERATION) {
      // Convert the payload to TextGenerationAction which should be the standardized format outlined by https://tools.hubteam.com/chadmin-ui/commands/text_generate
      return convertV1ActionToV2ToolInput({
        intent: TEXT_GENERATION,
        prompt: actionData.prompt,
        brandVoiceChannel: actionData.brandVoiceChannel || actionData.voiceChannel,
        brandVoiceSubChannel: actionData.brandVoiceSubChannel || actionData.voiceSubChannel,
        businessUnitId: actionData.businessUnitId,
        contentLanguage: actionData.contentLanguage || actionData.language,
        context: actionData.context,
        editorText: actionData.editorText,
        followingText: actionData.followingText,
        fromEmail: actionData.fromEmail,
        precedingText: actionData.precedingText,
        recipientCompany: actionData.recipientCompany,
        recipientLocation: actionData.recipientLocation,
        recipientName: actionData.recipientName,
        recipientRole: actionData.recipientRole,
        slug: actionData.slug,
        title: actionData.title,
        toEmail: actionData.toEmail,
        type: actionData.type
      });
    }
  }

  // TODO - refactor actions to utilize the same format, regardless of where they're being created from
  const updatedMessageAction = Object.assign({}, messageAction);
  // account for in-chat button actions that do not have a raw_action property
  if ((updatedMessageAction === null || updatedMessageAction === void 0 ? void 0 : updatedMessageAction.raw_action) === undefined && Object.keys(payload).length > 0) {
    updatedMessageAction.raw_action = payload;
  }
  if (updatedMessageAction.raw_action) {
    return convertV1ActionToV2ToolInput(updatedMessageAction.raw_action);
  }
  return undefined;
};
export const getIsV2ContentRewrite = message => {
  var _message$sections;
  if (!('sections' in message)) {
    return false;
  }
  return message === null || message === void 0 || (_message$sections = message.sections) === null || _message$sections === void 0 ? void 0 : _message$sections.some(section => {
    var _section$refineAction;
    return 'refineAction' in section && ((_section$refineAction = section.refineAction) === null || _section$refineAction === void 0 ? void 0 : _section$refineAction.payload.intent) === 'content_rewrite';
  });
};

/**
 * Check if the message is a content rewrite section for Copilot V2.
 *
 * The payload for V2 is different from V1, so we need to check for
 * the intent within the refineAction. This is a workaround to prevent
 * messy refactors of the collaboration plugins to support v2. We can
 * revisit these once we sunset v1.
 */
export const getCopilotV2ActionIntent = (message, isInteractiveModePluginEnabled = false) => {
  var _message$query;
  // Return early for v1 messages
  if ((_message$query = message.query) !== null && _message$query !== void 0 && _message$query.intent) {
    var _message$query2;
    return (_message$query2 = message.query) === null || _message$query2 === void 0 ? void 0 : _message$query2.intent;
  }
  let v2ActionActionIntent = undefined;

  // The collaborate section for V2 will have a refineAction with the intent
  const isV2ContentRewrite = getIsV2ContentRewrite(message);
  // If both are enabled, go with rewrite - first!
  if (isV2ContentRewrite) {
    v2ActionActionIntent = 'content_rewrite';
  } else if (isInteractiveModePluginEnabled) {
    v2ActionActionIntent = 'interactive_mode';
  }
  // Defaults to undefined
  return v2ActionActionIntent;
};

/**
 * Get the sections to display for an action in collab mode.
 *
 * Supports filtering only COLLABORATE sections for Copilot V2
 * so we can narrow to sections that have refineAction.
 */
export const getActionSections = (message, isUngatedForCopilotV2) => {
  if (!('sections' in message)) {
    return [];
  }
  if (isUngatedForCopilotV2) {
    return message.sections.filter(section => ['COLLABORATE'].includes(section.type) && 'refineAction' in section).map(section => {
      const _ref = section,
        {
          refineAction
        } = _ref,
        rest = _objectWithoutPropertiesLoose(_ref, _excluded2);
      return Object.assign({}, rest, {
        type: 'ACTION',
        actions: [refineAction]
      });
    });
  }
  return message.sections.filter(section => section.type === 'ACTION');
};